import { councilByName, trackerSearchUrls } from '../config/registry';

const TRACKER_SEARCH_URLS = trackerSearchUrls();

const LISMORE_SEARCH_URL = 'https://www.lismore-nsw.altitudelg.com/e-services/daEnquiryInit.do?doc_typ=5&nodeNum=162748';
const LISMORE_CDC_SEARCH_URL = 'https://www.lismore-nsw.altitudelg.com/e-services/daEnquiryInit.do?doc_typ=8&nodeNum=162749';

function isLismoreTrackerUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && ['tracker.lismore.nsw.gov.au', 'www.lismore-nsw.altitudelg.com'].includes(parsed.hostname);
  } catch {
    return false;
  }
}

const BALLINA_ADVANCED_SEARCH_URL = 'https://da.ballina.nsw.gov.au/Application/AdvancedSearchResult';

const GENERIC_COUNCIL_PAGES = [
  'www.ballina.nsw.gov.au/Speciality-Pages/Development-Applications',
  'www.lismore.nsw.gov.au/Building-and-planning/Development-Applications-in-Lismore',
];

function isUsefulCouncilSourceUrl(url) {
  if (!url || isLismoreTrackerUrl(url)) return false;

  try {
    const parsed = new URL(url);
    const hostAndPath = `${parsed.hostname}${parsed.pathname}`;
    if (parsed.protocol !== 'https:' || /(?:\.(?:pdf|docx?|xlsx?|zip)(?:$|[?#])|\/(?:document|download)(?:s|\/|$)|getElectronicDocumentContents|fileName=)/i.test(parsed.pathname + parsed.search)) return false;

    if (GENERIC_COUNCIL_PAGES.some(page => hostAndPath.includes(page))) return false;

    return [
      'datracker.byron.nsw.gov.au',
      'da.ballina.nsw.gov.au',
      'tracker.lismore.nsw.gov.au',
      'datracker.tweed.nsw.gov.au',
      'www.planningportal.nsw.gov.au',
    ].some(host => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`));
  } catch {
    return false;
  }
}

function asArray(value) {
  if (!value) return [];
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return []; }
  }
  return Array.isArray(value) ? value : [];
}

function relatedCouncilRecords(...sources) {
  return sources.flatMap(source => [
    ...asArray(source?.related_applications),
    ...asArray(source?.related_certificates),
  ]);
}

export function getRelatedCouncilRecordUrl(...sources) {
  const linkedRecord = relatedCouncilRecords(...sources).find(record => {
    if (!record || typeof record !== 'object' || !record.url) return false;
    try {
      const parsed = new URL(record.url);
      return parsed.protocol === 'https:' && isUsefulCouncilSourceUrl(record.url);
    } catch {
      return false;
    }
  });

  return linkedRecord?.url || null;
}

function ballinaDetailIdentity(url) {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' || parsed.hostname !== 'da.ballina.nsw.gov.au') return null;
    const match = parsed.pathname.match(/^\/Application\/ApplicationDetails\/(\d{3})\.(\d{4})\.(\d+)\.(\d+)\/?$/i);
    return match ? { type: match[1], year: match[2], number: Number(match[3]), version: Number(match[4]) } : null;
  } catch {
    return null;
  }
}

function isBallinaDevelopmentApplication(da) {
  return councilByName(da?.council)?.shortName === 'Ballina'
    && /^(?:Development Application|DA Modification|S4\.55|S96)$/i.test(String(da?.application_type || '').trim());
}

function matchingBallinaDaUrl(da, proData) {
  const ref = String(da?.council_application_number || da?.da_number || '').trim();
  const match = ref.match(/^(?:DA\s*)?(\d{4})[/.](\d+)(?:[/.](\d+))?$/i);
  if (!match) return null;
  const candidates = relatedCouncilRecords(da, proData).filter(record => {
    const identity = ballinaDetailIdentity(record?.url);
    return identity?.type === '010' && identity.year === match[1]
      && identity.number === Number(match[2])
      && (!match[3] || identity.version === Number(match[3]));
  });
  const urls = [...new Set(candidates.map(record => record.url.replace(/\/$/, '')))];
  return urls.length === 1 ? urls[0] : null;
}

export function getCouncilTrackerUrl(da, proData = null) {
  const ballinaDa = isBallinaDevelopmentApplication(da);
  const sourceIdentity = ballinaDetailIdentity(da?.source_url);
  // A certificate may share a number and property with its DA. It is not the
  // same application; use a verified matching DA link or the reference results.
  const sourceTypeMismatch = ballinaDa && sourceIdentity && sourceIdentity.type !== '010';
  if (isUsefulCouncilSourceUrl(da?.source_url) && !sourceTypeMismatch) return da.source_url;

  const relatedUrl = ballinaDa ? matchingBallinaDaUrl(da, proData) : getRelatedCouncilRecordUrl(da, proData);
  if (relatedUrl) return relatedUrl;

  if (councilByName(da?.council)?.shortName === 'Ballina') {
    const ballinaUrl = getBallinaSearchUrl(da);
    if (ballinaUrl) return ballinaUrl;
    if (isBallinaPortalOnlyRef(da)) return null;
  }
  // Altitude detail indices belong to a search session, not a stable DA. The
  // retired MasterView host redirects to the portal home page. Never reuse
  // either as a direct application link for another customer's browser.
  if (councilByName(da?.council)?.shortName === 'Lismore' || isLismoreTrackerUrl(da?.source_url)) {
    const ref = String(da?.council_application_number || da?.da_number || '').trim();
    const typePrefix = ref.match(/^(\d+)\.\d{4}\./)?.[1];
    const isCdc = typePrefix ? typePrefix === '8' : /^CDC-/i.test(ref) || /complying development/i.test(da?.application_type || '');
    return isCdc ? LISMORE_CDC_SEARCH_URL : LISMORE_SEARCH_URL;
  }
  return TRACKER_SEARCH_URLS[da?.council] || null;
}

function isBallinaPortalOnlyRef(da) {
  const ref = da?.council_application_number || da?.da_number || da?.planning_portal_app_number;
  return /^(PAN|CDC)-/i.test(String(ref || '').trim());
}

function getBallinaSearchUrl(da) {
  const ref = da?.council_application_number || da?.da_number || da?.planning_portal_app_number;
  if (!ref) return null;

  const councilRef = String(ref).trim();
  if (/^(PAN|CDC)-/i.test(councilRef)) return null;

  const params = new URLSearchParams({
    RemoveUndeterminedApplications: 'False',
    ShowOutstandingApplications: 'False',
    ShowExhibitedApplications: 'False',
    IncludeDocuments: 'False',
  });

  const daMatch = councilRef.match(/(?:DA\s*)?(\d{4})[/.](\d+)/i);
  if (daMatch) {
    params.set('ApplicationYear', daMatch[1]);
    params.set('ApplicationNumber', daMatch[2]);
    return `${BALLINA_ADVANCED_SEARCH_URL}?${params.toString()}`;
  }

  return null;
}

export { TRACKER_SEARCH_URLS, isUsefulCouncilSourceUrl, getBallinaSearchUrl };

function councilSearchKind(url) {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return null;
    const path = parsed.pathname.toLowerCase().replace(/\/$/, '');
    if (parsed.hostname === 'datracker.byron.nsw.gov.au' && path === '/masterviewui-external/home/search') return 'Byron';
    if (parsed.hostname === 'da.ballina.nsw.gov.au' && path === '/application/advancedsearchresult') return 'Ballina results';
    if (['www.tweed.nsw.gov.au', 'tweed.nsw.gov.au'].includes(parsed.hostname)
      && path === '/development-business/development-applications/da-tracker-property-search') return 'Tweed';
    return null;
  } catch {
    return null;
  }
}

function isRelatedCouncilHandoff(da, proData, url) {
  if (!url || !da) return false;
  const normalised = url.replace(/\/$/, '');
  if (typeof da.source_url === 'string' && da.source_url.replace(/\/$/, '') === normalised) return false;
  if (isBallinaDevelopmentApplication(da) && matchingBallinaDaUrl(da, proData) === normalised) return false;
  return relatedCouncilRecords(da, proData).some(record => typeof record?.url === 'string'
    && record.url.replace(/\/$/, '') === normalised);
}

export function councilLinkLabel(url, da = null, proData = null) {
  if (!url) return null;
  if (isLismoreTrackerUrl(url)) return 'Search Lismore council';
  const searchKind = councilSearchKind(url);
  if (searchKind === 'Byron' || searchKind === 'Tweed') return `Search ${searchKind} council`;
  if (searchKind === 'Ballina results') return 'View Ballina council results';
  if (isRelatedCouncilHandoff(da, proData, url)) return 'Open related council record';
  return /(?:\/search(?:[/?]|$)|AdvancedSearchResult|daEnquiry(?:Init)?\.do)/i.test(url)
    ? 'Search council by reference' : 'Open council application';
}


export function councilSearchHelp(da, url, proData = null) {
  if (!url) return null;
  const ref = String(da?.council_application_number || da?.da_number || '').trim();
  if (isLismoreTrackerUrl(url)) {
    // Council's form accepts number/year, not our type.year.number.version ID.
    // Keep the original identifier on the application to distinguish amendments.
    const dotted = ref.match(/^(?:5|8)\.(\d{4})\.(\d+)\.\d+$/);
    const yearFirst = ref.match(/^(\d{4})\/(\d+)(?:\/\d+)?$/);
    const numberFirst = ref.match(/^(\d+)\/(\d{4})(?:\/\d+)?$/);
    const reference = dotted ? `${dotted[2]}/${dotted[1]}`
      : yearFirst ? `${yearFirst[2]}/${yearFirst[1]}`
        : numberFirst ? `${numberFirst[1]}/${numberFirst[2]}` : null;
    return {
      reference,
      message: reference
        ? `Lismore opens a search form. Enter ${reference} in Application Number, then choose the matching address and application.`
        : 'Lismore opens a search form. Use Street Name to find the property.',
    };
  }
  const searchKind = councilSearchKind(url);
  if (searchKind === 'Byron') {
    const dotted = ref.match(/^\d+\.(\d{4})\.(\d+)\.\d+$/);
    return {
      reference: dotted ? String(Number(dotted[2])) : null,
      message: dotted
        ? `Enter ${dotted[1]} in Application Year and ${Number(dotted[2])} in Application Number, then choose the matching address and application.`
        : 'Use Street Name and Suburb in the council search to find the property.',
    };
  }
  if (searchKind === 'Ballina results') {
    return { reference: null, message: 'Choose the matching application type, reference and address from the council results.' };
  }
  if (searchKind === 'Tweed') {
    return { reference: null, message: 'Open DA Tracker or Property search on the council page to find the matching address and application.' };
  }
  if (isRelatedCouncilHandoff(da, proData, url)) {
    return { reference: null, message: 'This is a related council record. Check its application number and address.' };
  }
  return councilLinkLabel(url) === 'Search council by reference' && ref
    ? { reference: ref, message: `Enter ${ref} in the council search to locate this application.` }
    : null;
}
